import { useQuery } from "@tanstack/react-query";
import type { Room, User } from "./types";
import { apiGet } from "./api";
import { useSession } from "./auth";

interface ServerRoom {
  id: string; organization_id?: string; name?: string | null; kind?: string;
  topic?: string | null; last_message_at?: number | null; unread_count?: number; pinned?: boolean; starred?: boolean; last_read_message_id?: string | null;
}
export function toClientRoom(r: ServerRoom): Room {
  // 名前の無い DM は内部 ID を表示せず、モバイル版（mobile/src/lib/wire.ts）と同じ表示名にする。
  return { id: r.id, workspaceId: r.organization_id ?? "", name: r.name || (r.kind === "dm" ? "ダイレクトメッセージ" : r.id),
    kind: r.kind === "dm" ? "dm" : r.kind === "group" ? "group" : "channel",
    lastReadMessageId: r.last_read_message_id || undefined, unreadCount: r.unread_count ?? 0, pinned: r.starred ?? r.pinned ?? false,
    lastMessageAt: r.last_message_at ? new Date(r.last_message_at).toISOString() : undefined };
}
export function useRooms() {
  const session = useSession();
  const query = useQuery({ queryKey: ["rooms", session?.user.id], enabled: !!session,
    queryFn: async () => {
      const result = await apiGet<{ rooms: ServerRoom[] }>("/v1/rooms");
      return result.rooms.map(toClientRoom);
    }, refetchInterval: 30_000 });
  return { rooms: query.data ?? [], loading: query.isPending, error: query.error,
    isFallback: false, refresh: query.refetch };
}
export type RoomMember = User & { role: string };
export function useRoomMembers(roomId?: string) {
  const session = useSession();
  const query = useQuery({ queryKey: ["room-members", roomId, session?.user.id], enabled: !!roomId && !!session,
    queryFn: async (): Promise<RoomMember[]> => {
      const result = await apiGet<{ members: { user_id: string; role?: string; display_name?: string; email?: string; avatar_url?: string }[] }>(`/v1/rooms/${encodeURIComponent(roomId!)}/members`);
      return result.members.map((member) => ({ id: member.user_id, email: member.email ?? "",
        displayName: member.display_name || (member.user_id === session?.user.id ? session.user.displayName : member.user_id),
        avatarUrl: member.avatar_url, role: member.role || "member" }));
    } });
  return query;
}
